import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { usePortal } from '../context/PortalContext';
import { API_URL, otltAPI, authAPI, organizationAPI } from '../services/api';
import { EyeIcon, EyeSlashIcon, ShieldCheckIcon, SparklesIcon, KeyIcon } from '@heroicons/react/24/outline';
import { loadGoogleSDK } from '../utils/loadAuthSDK';
import { getDeviceId } from '../utils/deviceFingerprint';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

const Login = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [lockoutInfo, setLockoutInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [organizationName, setOrganizationName] = useState(''); // Empty by default - only set if org exists
  const [pendingLoginData, setPendingLoginData] = useState(null);
  const [isRegularUser, setIsRegularUser] = useState(false);
  const [facilityName, setFacilityName] = useState('');
  const { login, googleSignIn, refreshUser } = useAuth();
  const { portalUser, portalLoading } = usePortal();
  const navigate = useNavigate();
  const [googleLoading, setGoogleLoading] = useState(false);
  const [twoFactorSetup, setTwoFactorSetup] = useState(null); // { setup_token, qr_code, secret, backup_codes }
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  // B6: enforced 2FA enrollment state — when an admin/doctor is required
  // to set up 2FA, we walk them through QR → verify → login.
  const [enforced2FA, setEnforced2FA] = useState(null); // { setup_token, message }
  const [enforcedQrCode, setEnforcedQrCode] = useState('');
  const [enforcedSecret, setEnforcedSecret] = useState('');
  const [enforcedBackupCodes, setEnforcedBackupCodes] = useState([]);
  const [enforcedStep, setEnforcedStep] = useState('loading'); // loading, qr, verify, error
  const [enforcedError, setEnforcedError] = useState('');
  const [emailOtpMode, setEmailOtpMode] = useState(null); // { username, signup_email }
  const [passwordExpired, setPasswordExpired] = useState(null); // { username, message, expiry_token }
  // New-device verification: { username, device_token, device_brief, message }
  const [deviceVerification, setDeviceVerification] = useState(null);
  const [deviceOtpCode, setDeviceOtpCode] = useState('');
  const [deviceOtpLoading, setDeviceOtpLoading] = useState(false);
  const [deviceOtpMessage, setDeviceOtpMessage] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [emailOtpCode, setEmailOtpCode] = useState('');
  const [emailOtpLoading, setEmailOtpLoading] = useState(false);
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailOtpMessage, setEmailOtpMessage] = useState('');
  const [demoCredentials, setDemoCredentials] = useState(null);
  const [rememberMe, setRememberMe] = useState(() => {
    // Pre-check the box if a remembered username exists
    return !!localStorage.getItem('rememberedUsername');
  });
  const [google2faPending, setGoogle2faPending] = useState(null); // { pending_token, username }
  const googleButtonRef = useRef(null);
  const googleTimeoutRef = useRef(null);
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = React.useRef(null);

  // Portal gate: if portal user is loaded but can't access app, redirect
  useEffect(() => {
    if (!portalLoading && portalUser && !portalUser.can_access_app) {
      navigate('/portal/dashboard');
    }
  }, [portalUser, portalLoading, navigate]);

  // Quick backend health check on mount
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 5000);
    fetch(`${API_URL}/health`, { signal: controller.signal, mode: 'cors' })
      .then((res) => {
        clearTimeout(timer);
        if (cancelled) return;
        if (!res.ok) {
          setError('Server health check failed. The backend may be starting up — please wait a moment and try again.');
        }
      })
      .catch(() => {
        clearTimeout(timer);
        if (cancelled) return;
        setError('Cannot reach the server. Please make sure the backend is running and try again.');
      });
    return () => { cancelled = true; controller.abort(); };
  }, []);

  // Auto-redirect if user is already logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');

    if (token && userStr) {
      try {
        // Check if token is still valid
        const payload = JSON.parse(atob(token.split('.')[1]));
        const isExpired = payload.exp * 1000 < Date.now();

        if (!isExpired) {
          // User is already logged in, redirect to setup or patients
          try {
            const u = JSON.parse(userStr);
            navigateAfterLogin(u.role, u.is_tenant_owner);
          } catch {
            navigate('/app', { replace: true });
          }
          return;
        }
      } catch (e) {
        // Invalid token, continue with login page
      }
    }

    fetchOrganizationInfo();
    checkPendingLogin();
    checkOTLT(); // Check for OTLT token

    // Restore remembered username (if "Remember me" was previously checked)
    const remembered = localStorage.getItem('rememberedUsername');
    if (remembered && !sessionStorage.getItem('demoData') && !sessionStorage.getItem('pendingLogin')) {
      setUsername(remembered);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Preload the MainLayout chunk (which includes all app modules) in the
  // background while the user is on the login page. By the time they enter
  // credentials and click Sign In, the chunk is already in the browser's
  // cache and the transition into the app is instant.
  useEffect(() => {
    const trigger = () => import('../components/Layout/MainLayout');
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(trigger, { timeout: 3000 });
      return () => window.cancelIdleCallback(id);
    } else {
      const timer = setTimeout(trigger, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Auto Google sign-in when arriving from portal with googleCredential
  useEffect(() => {
    const credential = location.state?.googleCredential;
    if (credential && !google2faPending) {
      // Clear the state so it doesn't re-trigger on re-render
      navigate(location.pathname, { replace: true, state: {} });
      (async () => {
        setGoogleLoading(true);
        setError('');
        try {
          const result = await googleSignIn(credential);
          if (result.success) {
            navigateAfterLoginFromStorage();
          } else if (result.requires_2fa) {
            setGoogle2faPending({ pending_token: result.pending_token, username: result.username });
          } else {
            setError(result.error || 'Google sign-in failed');
          }
        } catch (err) {
          setError(err.message || 'Google sign-in failed');
        } finally {
          setGoogleLoading(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Check if CAPTCHA is required for this IP
  const checkCaptchaNeeded = async () => {
    try {
      const resp = await authAPI.checkCaptchaRequired();
      if (resp.data.required) {
        setCaptchaRequired(true);
      }
    } catch {
      // Silently ignore — don't block login if endpoint unavailable
    }
  };

  useEffect(() => {
    checkCaptchaNeeded();
    return () => {
      if (googleTimeoutRef.current) clearTimeout(googleTimeoutRef.current);
    };
  }, []);

  // Load Turnstile script when CAPTCHA is required
  useEffect(() => {
    if (captchaRequired && TURNSTILE_SITE_KEY && !document.getElementById('turnstile-script')) {
      const script = document.createElement('script');
      script.id = 'turnstile-script';
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      document.head.appendChild(script);
    }
  }, [captchaRequired]);

  const checkPendingLogin = () => {
    // Check for demo credentials from demo signup
    const demoData = sessionStorage.getItem('demoData');
    if (demoData) {
      try {
        const parsed = JSON.parse(demoData);
        if (parsed.credentials && parsed.credentials.length > 0) {
          const adminCred = parsed.credentials.find(c => c.role === 'admin' || c.role === 'ADMIN') || parsed.credentials[0];
          setUsername(adminCred.username);
          setPassword(adminCred.password);
          setDemoCredentials({ ...parsed, credentials: [adminCred] });
          setFacilityName(parsed.facility_name || '');
        }
      } catch (e) {
        sessionStorage.removeItem('demoData');
      }
    }

    // Check for activation success message
    const activationSuccess = sessionStorage.getItem('activationSuccess');
    if (activationSuccess) {
      setSuccessMessage(activationSuccess);
      sessionStorage.removeItem('activationSuccess');
    }
    
    // Check if redirected from payment success with auto-login flag.
    // Credentials are passed via router state (memory-only) when coming from
    // the portal login modal (OTP/2FA continuation); sessionStorage is kept
    // only as a fallback for legacy flows.
    const pendingLogin = sessionStorage.getItem('pendingLogin');
    const stateCredentials = location.state?.credentials;
    if (stateCredentials && location.state?.autoLogin) {
      setPendingLoginData(stateCredentials);
      setUsername(stateCredentials.username);
      setPassword(stateCredentials.password);

      // Auto-login immediately
      setTimeout(() => {
        handleAutoLogin(stateCredentials);
      }, 100); // Reduced from 500ms to 100ms
    } else if (pendingLogin && location.state?.autoLogin) {
      try {
        const loginData = JSON.parse(pendingLogin);
        setPendingLoginData(loginData);
        setUsername(loginData.username);
        setPassword(loginData.password);

        // Auto-login immediately
        setTimeout(() => {
          handleAutoLogin(loginData);
        }, 100); // Reduced from 500ms to 100ms
      } catch (error) {
        console.error('Failed to parse pending login data:', error);
        sessionStorage.removeItem('pendingLogin');
      }
    }
  };

  const handleAutoLogin = async (loginData) => {
    setLoading(true);

    try {
      // Add timeout to prevent infinite loading
      const deviceId = getDeviceId();
      const loginPromise = login(loginData.username, loginData.password, null, null, deviceId);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Login timeout')), 15000)
      );

      const result = await Promise.race([loginPromise, timeoutPromise]);

      if (result.success) {
        // Clear loading immediately before showing modal or navigating
        setLoading(false);
        // For new admin users, redirect to setup checklist
        if (loginData.isNewAdmin) {
          navigate('/app/setup', { replace: true });
        } else {
          navigateAfterLoginFromStorage();
        }
      } else if (result.requires_email_otp) {
        // Demo admin: email OTP verification required
        setLoading(false);
        setEmailOtpMode({ username: result.username, signup_email: result.signup_email, password_verified_token: result.password_verified_token });
        // Automatically send OTP
        try {
          const sendResp = await authAPI.sendDemoOtp(result.username, result.password_verified_token);
          setEmailOtpSent(true);
          setEmailOtpMessage(sendResp.data.message);
        } catch (sendErr) {
          setError(sendErr.response?.data?.detail || 'Failed to send verification code');
        }
      } else if (result.requires_device_verification) {
        // New device detected — show device OTP UI (backend already auto-sent code)
        setLoading(false);
        setDeviceVerification({
          username: result.username,
          device_token: result.device_token,
          device_brief: result.device_brief,
          message: result.message,
        });
        setDeviceOtpCode('');
        setDeviceOtpMessage(result.message);
      } else if (result.requires_2fa) {
        // Existing 2FA user — need to enter TOTP code
        setLoading(false);
        setTwoFactorSetup({ existing_2fa: true, username: result.username });
      } else if (result.requires_2fa_setup) {
        // B6: 2FA setup required (admin/tenant-owner enforcement) —
        // kick off the QR enrollment flow.
        setLoading(false);
        setEnforced2FA({ setup_token: result.setup_token, message: result.message });
        handleEnforced2FASetup(result.setup_token);
      } else if (result.password_expired) {
        // Password expired — redirect to password change
        setLoading(false);
        setPasswordExpired({
          username: result.username,
          message: result.message,
          expiry_token: result.expiry_token,
        });
      } else {
        setError(result.error);
        sessionStorage.removeItem('pendingLogin');
        setLoading(false);
      }
    } catch (error) {
      console.error('Auto-login error:', error);
      setError(error.message === 'Login timeout'
        ? 'Login is taking too long. Please try again manually.'
        : 'Login failed. Please try again.');
      sessionStorage.removeItem('pendingLogin');
      setLoading(false);
    }
  };

  const checkOTLT = async () => {
    // Check for OTLT token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (token) {
      setLoading(true);

      try {
        const response = await otltAPI.exchangeToken(token);
        const data = response.data;

        // Prefill login form
        setUsername(data.username);
        setOrganizationName(data.facility_name);
        if (data.tenant_slug) {
          setFacilityName(data.facility_name);
        }

        // Clear token from URL
        window.history.replaceState({}, document.title, window.location.pathname);

        // Focus password field
        setTimeout(() => {
          document.querySelector('input[type="password"]')?.focus();
        }, 100);

      } catch (error) {
        console.error('OTLT exchange error:', error);
        setError('Login link expired or invalid. Please log in normally.');
      } finally {
        setLoading(false);
      }
    }
  };

  // After successful login, check if admin needs setup redirect.
  // Non-admins always go to /app/patients. Admins with incomplete setup
  // go to /app/setup; admins with complete setup go to /app/patients.
  const navigateAfterLogin = async (userRole, isTenantOwner) => {
    const isAdmin = userRole === 'ADMIN' || userRole === 'SUPERADMIN' || isTenantOwner;
    if (!isAdmin) {
      navigate('/app', { replace: true });
      return;
    }
    try {
      const resp = await organizationAPI.getSetupStatus();
      if (resp.data && !resp.data.is_complete) {
        navigate('/app/setup', { replace: true });
      } else {
        navigate('/app', { replace: true });
      }
    } catch {
      // If setup status check fails, just go to patients
      navigate('/app', { replace: true });
    }
  };

  // Convenience: read user from localStorage (set by AuthContext.login)
  // and call navigateAfterLogin with the right role/owner flags.
  const navigateAfterLoginFromStorage = () => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      const role = u.role || '';
      const isOwner = !!u.is_tenant_owner;
      navigateAfterLogin(role, isOwner);
    } catch {
      navigate('/app', { replace: true });
    }
  };

  const fetchOrganizationInfo = async () => {
    // Don't fetch organization info on login page - it requires auth
    // Organization info will be fetched after successful login
    // This prevents infinite loading for new users who don't have org yet
    return;

    /* Keeping this code for reference - moved to post-login
    try {
      const response = await organizationAPI.getSettings();
      if (response.data) {
        if (response.data.name) {
          setOrganizationName(response.data.name);
        }
        // Set language from organization settings
        if (response.data.language) {
          i18n.changeLanguage(response.data.language);
        }
      }
    } catch (error) {
      // If settings don't exist yet (new user), no organization name will be shown
      // Organization settings not found - user may not have completed setup yet
    }
    */
  };

  const handleGoogleSignIn = async () => {
    if (!GOOGLE_CLIENT_ID) {
      setError('Google Sign-In is not configured. Set REACT_APP_GOOGLE_CLIENT_ID in your environment.');
      return;
    }
    setGoogleLoading(true);
    setError('');
    try {
      // Load Google Identity Services script on demand
      if (!window.google) {
        await loadGoogleSDK();
      }
      if (!window.google) {
        setError('Google Identity library failed to load');
        setGoogleLoading(false);
        return;
      }

      // Use renderButton + programmatic click — opens a proper popup
      // This is more reliable than prompt() (One Tap) which is often blocked
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response) => {
          if (googleTimeoutRef.current) clearTimeout(googleTimeoutRef.current);
          if (!response.credential) {
            setError('No credential returned from Google');
            setGoogleLoading(false);
            return;
          }
          try {
            const result = await googleSignIn(response.credential);
            if (result.success) {
              navigateAfterLoginFromStorage();
            } else if (result.requires_2fa) {
              setGoogle2faPending({
                pending_token: result.pending_token,
                username: result.username,
              });
            } else {
              setError(result.error || 'Google sign-in failed');
            }
          } catch (err) {
            setError(err.message || 'Google sign-in failed');
          } finally {
            setGoogleLoading(false);
          }
        },
      });

      // Render a hidden button and click it to trigger the Google popup
      if (googleButtonRef.current) {
        googleButtonRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(googleButtonRef.current, {
          type: 'standard',
          size: 'large',
          width: 0,
        });
        // Click the rendered button to open Google's popup
        setTimeout(() => {
          const btn = googleButtonRef.current?.querySelector('div[role="button"]') || googleButtonRef.current?.querySelector('button');
          if (btn) {
            btn.click();
          } else {
            // Fallback: use prompt() if renderButton didn't produce a clickable element
            window.google.accounts.id.prompt((notification) => {
              if (notification.isNotDisplayed() || notification.isSkippedMoment() || notification.isDismissedMoment()) {
                setGoogleLoading(false);
                setError('Google sign-in could not be displayed. Please try again.');
              }
            });
          }
        }, 100);
      } else {
        // Fallback to prompt
        window.google.accounts.id.prompt((notification) => {
          if (notification.isNotDisplayed() || notification.isSkippedMoment() || notification.isDismissedMoment()) {
            setGoogleLoading(false);
            setError('Google sign-in could not be displayed. Please try again.');
          }
        });
      }

      // Timeout fallback so a dismissed/blocked popup never hangs the UI
      googleTimeoutRef.current = setTimeout(() => {
        setGoogleLoading(false);
      }, 60000);
    } catch (err) {
      setError(err.message || 'Google sign-in failed. Please try again.');
      setGoogleLoading(false);
    }
  };

  const handleVerifyEmailOtp = async () => {
    if (!emailOtpCode || emailOtpCode.length !== 6) {
      setError('Please enter the 6-digit verification code');
      return;
    }
    setEmailOtpLoading(true);
    setError('');
    try {
      const resp = await authAPI.verifyDemoOtp(emailOtpMode.username, emailOtpCode);
      const { access_token, refresh_token, user } = resp.data;
      localStorage.setItem('token', access_token);
      if (refresh_token) {
        localStorage.setItem('refreshToken', refresh_token);
      }
      localStorage.setItem('user', JSON.stringify(user));
      await refreshUser();
      setEmailOtpMode(null);
      setEmailOtpCode('');
      navigateAfterLogin(user.role, user.is_tenant_owner);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Verification failed');
    } finally {
      setEmailOtpLoading(false);
    }
  };

  const handleResendEmailOtp = async () => {
    if (!emailOtpMode?.username) return;
    setEmailOtpLoading(true);
    setError('');
    try {
      const resp = await authAPI.sendDemoOtp(emailOtpMode.username, emailOtpMode.password_verified_token);
      setEmailOtpMessage(resp.data.message);
      setEmailOtpCode('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend code');
    } finally {
      setEmailOtpLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!twoFactorCode || twoFactorCode.length !== 6) {
      setError('Please enter the 6-digit code from your authenticator app');
      return;
    }
    setTwoFactorLoading(true);
    setError('');
    try {
      // Existing 2FA user — re-login with the TOTP code in the form body
      // (not as a URL query param, to avoid leaking it into nginx logs)
      const formData = new FormData();
      formData.append('username', username);
      formData.append('password', password);
      formData.append('two_factor_token', twoFactorCode);
      // Include captcha token if required — without it the backend rejects
      // the request with 400 "CAPTCHA verification required", causing the
      // 2FA retry to always fail once captcha is triggered.
      if (captchaRequired && captchaToken) {
        formData.append('turnstile_token', captchaToken);
      }
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Invalid 2FA code');
      }
      localStorage.setItem('token', data.access_token);
      if (data.refresh_token) {
        localStorage.setItem('refreshToken', data.refresh_token);
      }
      localStorage.setItem('user', JSON.stringify(data.user));
      setTwoFactorSetup(null);
      navigateAfterLogin(data.user.role, data.user.is_tenant_owner);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || '2FA verification failed');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  // B6: Enforced 2FA enrollment — fetch QR code from the backend using
  // the setup_token issued during login.
  const handleEnforced2FASetup = async (setupToken) => {
    setEnforcedStep('loading');
    setEnforcedError('');
    try {
      const resp = await authAPI.enforcedSetup2FA(setupToken);
      setEnforcedQrCode(resp.data.qr_code);
      setEnforcedSecret(resp.data.secret);
      setEnforcedBackupCodes(resp.data.backup_codes || []);
      setEnforcedStep('qr');
    } catch (err) {
      setEnforcedError(err.response?.data?.detail || 'Failed to initialize 2FA setup');
      setEnforcedStep('error');
    }
  };

  // B6: Verify the TOTP code and enable 2FA — returns a full access token
  // so the user can proceed without logging in again.
  const handleEnforced2FAVerify = async () => {
    if (!twoFactorCode || twoFactorCode.length !== 6) {
      setEnforcedError('Please enter the 6-digit code from your authenticator app');
      return;
    }
    setTwoFactorLoading(true);
    setEnforcedError('');
    try {
      const resp = await authAPI.enforcedVerify2FA(enforced2FA.setup_token, twoFactorCode);
      const { access_token, refresh_token, user } = resp.data;
      localStorage.setItem('token', access_token);
      if (refresh_token) {
        localStorage.setItem('refreshToken', refresh_token);
      }
      localStorage.setItem('user', JSON.stringify(user));
      setEnforced2FA(null);
      setTwoFactorCode('');
      navigateAfterLogin(user.role, user.is_tenant_owner);
    } catch (err) {
      setEnforcedError(err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleGoogleVerify2FA = async () => {
    if (!twoFactorCode || twoFactorCode.length !== 6) {
      setError('Please enter the 6-digit code from your authenticator app');
      return;
    }
    setTwoFactorLoading(true);
    setError('');
    try {
      const resp = await authAPI.googleVerify2FA(google2faPending.pending_token, twoFactorCode);
      const { access_token, refresh_token, user } = resp.data;
      localStorage.setItem('token', access_token);
      if (refresh_token) {
        localStorage.setItem('refreshToken', refresh_token);
      }
      localStorage.setItem('user', JSON.stringify(user));
      setGoogle2faPending(null);
      setTwoFactorCode('');
      navigateAfterLogin(user.role, user.is_tenant_owner);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || '2FA verification failed');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setLockoutInfo(null);
      setLoading(true);

      const deviceId = getDeviceId();
      const result = await login(username, password, null, captchaRequired ? captchaToken : null, deviceId);

      if (result.success) {
        // Persist or clear remembered username based on checkbox state
        if (rememberMe) {
          localStorage.setItem('rememberedUsername', username);
        } else {
          localStorage.removeItem('rememberedUsername');
        }
        navigateAfterLoginFromStorage();
      } else if (result.requires_device_verification) {
        // New device detected — backend already auto-sent an OTP code
        setDeviceVerification({
          username: result.username,
          device_token: result.device_token,
          device_brief: result.device_brief,
          message: result.message,
        });
        setDeviceOtpCode('');
        setDeviceOtpMessage(result.message);
      } else if (result.requires_email_otp) {
        // Demo admin: email OTP verification required
        setEmailOtpMode({ username: result.username, signup_email: result.signup_email, password_verified_token: result.password_verified_token });
        // Automatically send OTP
        try {
          const sendResp = await authAPI.sendDemoOtp(result.username, result.password_verified_token);
          setEmailOtpSent(true);
          setEmailOtpMessage(sendResp.data.message);
        } catch (sendErr) {
          setError(sendErr.response?.data?.detail || 'Failed to send verification code');
        }
      } else if (result.requires_2fa) {
        // Existing 2FA user — need to enter TOTP code
        setTwoFactorSetup({ existing_2fa: true, username: result.username });
      } else if (result.requires_2fa_setup) {
        // B6: Admin/doctor enforced 2FA enrollment — kick off QR setup
        setEnforced2FA({ setup_token: result.setup_token, message: result.message });
        handleEnforced2FASetup(result.setup_token);
      } else if (result.password_expired) {
        // Password expired — show password change form
        setPasswordExpired({
          username: result.username,
          message: result.message,
          expiry_token: result.expiry_token,
        });
      } else {
        // Check if error message contains lockout information
        const errorMsg = result.error;

        // Parse lockout time — backend says "try again in X minutes"
        // (appears in account-locked and IP-banned messages)
        const lockoutMatch = errorMsg.match(/try again in (\d+) minutes/);
        if (lockoutMatch) {
          setLockoutInfo({
            isLocked: true,
            remainingMinutes: parseInt(lockoutMatch[1])
          });
        }

        // Parse remaining attempts — backend says "X attempt(s) remaining"
        const attemptsMatch = errorMsg.match(/(\d+) attempt\(s\) remaining/);
        if (attemptsMatch) {
          setLockoutInfo({
            isLocked: false,
            remainingAttempts: parseInt(attemptsMatch[1])
          });
        }

        // Check if CAPTCHA is now required (after failed attempt)
        if (!captchaRequired) {
          await checkCaptchaNeeded();
        }

        // Reset CAPTCHA token so user must re-verify
        setCaptchaToken(null);
        if (captchaRequired && window.turnstile && captchaRef.current) {
          window.turnstile.reset(captchaRef.current);
        }

        setError(errorMsg);
      }

      } catch (error) {
        console.error("Error in handleSubmit:", error);
        setError(error?.message || 'Login failed. Please try again.');
      } finally {
        setLoading(false);
      }
  };

  // New-device verification: verify the OTP code sent to the user's email
  const handleDeviceOtpVerify = async (e) => {
    e?.preventDefault();
    if (!deviceVerification?.username) return;
    if (!deviceOtpCode || deviceOtpCode.length !== 6) {
      setError('Please enter the 6-digit code.');
      return;
    }
    setDeviceOtpLoading(true);
    setError('');
    try {
      const deviceId = getDeviceId();
      const resp = await authAPI.verifyDeviceOtp(
        deviceVerification.username,
        deviceOtpCode,
        deviceId,
        rememberDevice
      );
      const data = resp.data;
      const { access_token, refresh_token, user } = data;
      localStorage.setItem('token', access_token);
      try {
        document.cookie = `ospito_token=${encodeURIComponent(access_token)}; path=/; SameSite=Strict${window.location.protocol === 'https:' ? '; Secure' : ''}`;
      } catch (e) { /* ignore */ }
      if (refresh_token) localStorage.setItem('refreshToken', refresh_token);
      localStorage.setItem('user', JSON.stringify(user));
      setDeviceVerification(null);
      setDeviceOtpCode('');
      // Use the auth context's setUser by calling refreshUser if available,
      // otherwise navigate (the token is in localStorage so the app will load).
      navigateAfterLoginFromStorage();
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed. Please try again.');
    } finally {
      setDeviceOtpLoading(false);
    }
  };

  // New-device verification: resend the OTP code
  const handleDeviceOtpResend = async () => {
    if (!deviceVerification?.username) return;
    setDeviceOtpLoading(true);
    setError('');
    try {
      const resp = await authAPI.sendDeviceOtp(deviceVerification.username, deviceVerification.device_token);
      setDeviceOtpMessage(resp.data.message || 'A new code has been sent.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend code. Please try again.');
    } finally {
      setDeviceOtpLoading(false);
    }
  };

  return (
    <div className="login-page min-h-screen relative flex items-center justify-center px-4">
      {/* Background - Same as Hero Section */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <img 
          src="/images/hero-gradient.svg" 
          alt="" 
          className="absolute inset-0 w-full h-full object-cover dark:hidden"
          aria-hidden="true"
        />
        {/* Dark mode background */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 hidden dark:block" />
      </div>
      
      <div className="relative z-10 max-w-md w-full">
        {/* Logo and Title */}
        <div className="text-center mb-5">
          <div className="flex justify-center mb-1">
            <Link to="/" className="flex items-baseline group">
              <span className="text-4xl font-bold bg-gradient-to-r from-orange-500 via-orange-600 to-amber-500 bg-clip-text text-transparent tracking-tight group-hover:opacity-80 transition-opacity" style={{ fontFamily: "'Baloo Bhai 2', sans-serif", fontWeight: 700 }}>
                Ospito
              </span>
              <span className="text-2xl font-bold bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent -ml-0.5 -translate-y-1.5 inline-block" style={{ fontFamily: "'Baloo Bhai 2', sans-serif", fontWeight: 700 }}>
                +
              </span>
            </Link>
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 font-medium tracking-wide">Hospital Management System</div>
          {!isRegularUser && organizationName && (
            <p className="text-xs text-gray-600 dark:text-gray-300 font-medium mt-1">
              {organizationName}
            </p>
          )}
        </div>

        {/* Login Form */}
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-2xl p-6 border border-white/20 dark:border-gray-700/50">
          <h2 className="text-base font-bold text-gray-800 dark:text-white mb-0.5 text-center">{t('login.title')}</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-4 text-[11px] text-center">{t('login.welcome')} {t('app.name')}</p>

          {/* Auto-login progress message */}
          {pendingLoginData && loading && (
            <div className="bg-teal-50 border border-teal-200 px-3 py-2 rounded-lg mb-3">
              <div className="flex items-start gap-2">
                <svg className="animate-spin h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <div className="flex-1">
                  <p className="text-xs font-semibold text-teal-800">Signing you in…</p>
                  <p className="text-[10px] text-teal-700 mt-0.5">Setting up your account</p>
                  <button
                    type="button"
                    onClick={() => {
                      setLoading(false);
                      setPendingLoginData(null);
                      sessionStorage.removeItem('pendingLogin');
                    }}
                    className="text-[10px] text-teal-600 hover:text-teal-800 underline mt-1"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {successMessage && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-1.5 rounded-lg mb-3 text-xs font-medium text-center">
              {successMessage}
            </div>
          )}

          {demoCredentials && demoCredentials.credentials.some((c) => c.username === username.trim()) && (
            <div className="bg-teal-50 border border-teal-200 rounded-lg mb-3 overflow-hidden">
              <div className="px-3 py-2 bg-teal-500/10 border-b border-teal-200">
                <p className="text-xs font-bold text-teal-800 text-center">{demoCredentials.facility_name} — Demo expires in {demoCredentials.expires_in_hours}h</p>
              </div>
              <div className="p-2 space-y-1">
                {demoCredentials.credentials.map((cred, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => { setUsername(cred.username); setPassword(cred.password); }}
                    className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-[11px] transition-all ${
                      username === cred.username
                        ? 'bg-teal-500 text-white'
                        : 'bg-white hover:bg-teal-50 text-gray-700 border border-teal-100'
                    }`}
                  >
                    <span className="font-medium">Demo as {cred.display_name}</span>
                    <span className="font-mono text-[9px] opacity-75">{cred.username}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="text-red-600 px-3 py-1 mb-2 text-xs font-medium text-center">
              {error}
            </div>
          )}

          {lockoutInfo?.isLocked && (
            <div className="bg-orange-50 border border-orange-200 px-3 py-2 rounded-lg mb-3">
              <div className="flex items-start gap-2">
                <svg className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-xs font-semibold text-orange-800">Account Locked</p>
                  <p className="text-[10px] text-orange-700 mt-0.5">
                    Unlocked in {lockoutInfo.remainingMinutes} min.
                  </p>
                </div>
              </div>
            </div>
          )}

          {lockoutInfo?.remainingAttempts && (
            <div className="px-3 py-1 mb-3">
              <div className="flex items-center justify-center gap-1.5">
                <svg className="h-3.5 w-3.5 text-yellow-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <p className="text-[11px] font-medium text-yellow-700">
                  {lockoutInfo.remainingAttempts} attempt{lockoutInfo.remainingAttempts === 1 ? '' : 's'} remaining before lockout
                </p>
              </div>
            </div>
          )}

          {/* Email OTP Verification Panel */}
          {emailOtpMode ? (
            <div className="space-y-3">
              <div className="text-center mb-3">
                <div className="inline-flex items-center justify-center w-11 h-11 bg-teal-50 dark:bg-teal-900/30 rounded-full mb-2">
                  <ShieldCheckIcon className="h-5 w-5 text-teal-500" />
                </div>
                <h3 className="text-sm font-semibold text-gray-800 dark:text-white">
                  Enter Verification Code
                </h3>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                  {emailOtpSent ? emailOtpMessage : 'Sending verification code…'}
                </p>
              </div>

              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={emailOtpCode}
                onChange={(e) => setEmailOtpCode(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => { if (e.key === 'Enter' && emailOtpCode.length === 6) handleVerifyEmailOtp(); }}
                className="w-full px-3 py-2.5 text-center text-xl font-bold tracking-[0.4em] rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-4 focus:ring-teal-500/40 focus:border-teal-500 transition-all duration-150 cursor-text"
                placeholder="000000"
                autoFocus
              />

              <button
                type="button"
                onClick={handleVerifyEmailOtp}
                disabled={emailOtpLoading || emailOtpCode.length !== 6}
                className="w-full py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-sm font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
              >
                {emailOtpLoading ? 'Verifying…' : 'Verify & Continue'}
              </button>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={handleResendEmailOtp}
                  disabled={emailOtpLoading}
                  className="text-[11px] text-teal-600 hover:text-teal-700 font-medium"
                >
                  Resend code
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEmailOtpMode(null);
                    setEmailOtpCode('');
                    setEmailOtpSent(false);
                    setEmailOtpMessage('');
                    setError('');
                  }}
                  className="text-[11px] text-gray-500 hover:text-gray-700"
                >
                  ← Back to login
                </button>
              </div>
            </div>
          ) : enforced2FA ? (
            <div className="space-y-3">
              {enforcedStep === 'loading' && (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
                </div>
              )}

              {enforcedStep === 'error' && (
                <>
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                    <p className="text-xs text-red-600 dark:text-red-400">{enforcedError}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleEnforced2FASetup(enforced2FA.setup_token)}
                    className="w-full py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-sm font-semibold rounded-lg hover:shadow-lg transition-all"
                  >
                    Try Again
                  </button>
                  <button
                    type="button"
                    onClick={() => { setEnforced2FA(null); setEnforcedError(''); setTwoFactorCode(''); }}
                    className="w-full text-[11px] text-gray-500 hover:text-gray-700"
                  >
                    ← Back to login
                  </button>
                </>
              )}

              {enforcedStep === 'qr' && (
                <>
                  <div className="text-center mb-2">
                    <div className="inline-flex items-center justify-center w-11 h-11 bg-teal-50 dark:bg-teal-900/30 rounded-full mb-2">
                      <ShieldCheckIcon className="h-5 w-5 text-teal-500" />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-white">
                      Set Up Two-Factor Authentication
                    </h3>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                      {enforced2FA.message || 'Your administrator requires 2FA. Scan this QR code with your authenticator app.'}
                    </p>
                  </div>

                  <div className="flex justify-center bg-white rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                    <img src={enforcedQrCode} alt="2FA QR Code" className="w-48 h-48" />
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2.5">
                    <p className="text-[10px] text-gray-500 mb-1">Can't scan? Enter this code manually:</p>
                    <code className="block text-[11px] font-mono text-gray-700 dark:text-gray-300 break-all">
                      {enforcedSecret}
                    </code>
                  </div>

                  {enforcedBackupCodes.length > 0 && (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-2.5">
                      <p className="text-[10px] font-semibold text-yellow-800 dark:text-yellow-400 mb-1">Backup Codes (save these!):</p>
                      <div className="grid grid-cols-2 gap-1">
                        {enforcedBackupCodes.map((c, i) => (
                          <code key={i} className="text-[10px] font-mono text-yellow-800 dark:text-yellow-400">{c}</code>
                        ))}
                      </div>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => setEnforcedStep('verify')}
                    className="w-full py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-sm font-semibold rounded-lg hover:shadow-lg transition-all"
                  >
                    Continue to Verification
                  </button>
                  <button
                    type="button"
                    onClick={() => { setEnforced2FA(null); setEnforcedStep('loading'); setTwoFactorCode(''); setEnforcedError(''); }}
                    className="w-full text-[11px] text-gray-500 hover:text-gray-700"
                  >
                    ← Back to login
                  </button>
                </>
              )}

              {enforcedStep === 'verify' && (
                <>
                  <div className="text-center mb-2">
                    <div className="inline-flex items-center justify-center w-11 h-11 bg-teal-50 dark:bg-teal-900/30 rounded-full mb-2">
                      <ShieldCheckIcon className="h-5 w-5 text-teal-500" />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-white">
                      Verify Your Setup
                    </h3>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                      Enter the 6-digit code from your authenticator app
                    </p>
                  </div>

                  {enforcedError && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-2.5">
                      <p className="text-xs text-red-600 dark:text-red-400">{enforcedError}</p>
                    </div>
                  )}

                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, ''))}
                    className="w-full px-3 py-2.5 text-center text-xl font-bold tracking-[0.4em] rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-4 focus:ring-teal-500/40 focus:border-teal-500 transition-all duration-150 cursor-text"
                    placeholder="000000"
                    autoFocus
                  />

                  <button
                    type="button"
                    onClick={handleEnforced2FAVerify}
                    disabled={twoFactorLoading || twoFactorCode.length !== 6}
                    className="w-full py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-sm font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
                  >
                    {twoFactorLoading ? 'Verifying…' : 'Verify & Enable'}
                  </button>

                  <button
                    type="button"
                    onClick={() => { setEnforcedStep('qr'); setEnforcedError(''); setTwoFactorCode(''); }}
                    className="w-full text-[11px] text-gray-500 hover:text-gray-700"
                  >
                    ← Back to QR code
                  </button>
                </>
              )}
            </div>
          ) : twoFactorSetup ? (
            <div className="space-y-3">
              <div className="text-center mb-3">
                <div className="inline-flex items-center justify-center w-11 h-11 bg-teal-50 dark:bg-teal-900/30 rounded-full mb-2">
                  <ShieldCheckIcon className="h-5 w-5 text-teal-500" />
                </div>
                <h3 className="text-sm font-semibold text-gray-800 dark:text-white">
                  Enter 2FA Code
                </h3>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                  Enter the 6-digit code from your authenticator app
                </p>
              </div>

              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, ''))}
                className="w-full px-3 py-2.5 text-center text-xl font-bold tracking-[0.4em] rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-4 focus:ring-teal-500/40 focus:border-teal-500 transition-all duration-150 cursor-text"
                placeholder="000000"
                autoFocus
              />

              <button
                type="button"
                onClick={handleVerify2FA}
                disabled={twoFactorLoading || (captchaRequired && !captchaToken)}
                className="w-full py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-sm font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
              >
                {twoFactorLoading ? 'Verifying…' : 'Verify & Continue'}
              </button>

              {/* Captcha widget — must be rendered here too, otherwise the
                  2FA retry always fails once captcha is triggered. */}
              {captchaRequired && TURNSTILE_SITE_KEY && (
                <div className="flex justify-center py-1.5">
                  <div
                    ref={captchaRef}
                    className="cf-turnstile"
                    data-sitekey={TURNSTILE_SITE_KEY}
                    data-theme="light"
                    data-callback={(token) => setCaptchaToken(token)}
                    data-expired-callback={() => setCaptchaToken(null)}
                    data-error-callback={() => setCaptchaToken(null)}
                  />
                </div>
              )}

              <button
                type="button"
                onClick={() => {
                  setTwoFactorSetup(null);
                  setTwoFactorCode('');
                  setError('');
                }}
                className="w-full text-[11px] text-gray-500 hover:text-gray-700"
              >
                ← Back to login
              </button>
            </div>
          ) : passwordExpired ? (
            <div className="space-y-3">
              <div className="text-center mb-3">
                <div className="inline-flex items-center justify-center w-11 h-11 bg-amber-50 dark:bg-amber-900/30 rounded-full mb-2">
                  <KeyIcon className="h-5 w-5 text-amber-500" />
                </div>
                <h3 className="text-sm font-semibold text-gray-800 dark:text-white">
                  Password Expired
                </h3>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                  {passwordExpired.message || 'Your password has expired. Please set a new password to continue.'}
                </p>
              </div>

              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="New password"
              />
              <input
                type="password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="Confirm new password"
              />

              <button
                type="button"
                onClick={async () => {
                  if (!newPassword || newPassword.length < 8) {
                    setError('Password must be at least 8 characters.');
                    return;
                  }
                  if (newPassword !== confirmNewPassword) {
                    setError('Passwords do not match.');
                    return;
                  }
                  try {
                    setTwoFactorLoading(true);
                    await authAPI.changeExpiredPassword(passwordExpired.expiry_token, newPassword);
                    setError('');
                    setPasswordExpired(null);
                    setNewPassword('');
                    setConfirmNewPassword('');
                    // Re-attempt login with the new password
                    const result = await login(passwordExpired.username, newPassword);
                    if (result.success) {
                      navigateAfterLoginFromStorage();
                    } else {
                      setError(result.error || 'Please log in with your new password.');
                    }
                  } catch (err) {
                    setError(err.response?.data?.detail || 'Failed to change password.');
                  } finally {
                    setTwoFactorLoading(false);
                  }
                }}
                disabled={twoFactorLoading}
                className="w-full py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-sm font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
              >
                {twoFactorLoading ? 'Changing…' : 'Set New Password'}
              </button>

              <button
                type="button"
                onClick={() => {
                  setPasswordExpired(null);
                  setNewPassword('');
                  setConfirmNewPassword('');
                  setError('');
                }}
                className="w-full text-[11px] text-gray-500 hover:text-gray-700"
              >
                ← Back to login
              </button>
            </div>
          ) : deviceVerification ? (
            <div className="space-y-3">
              <div className="text-center mb-3">
                <div className="inline-flex items-center justify-center w-11 h-11 bg-blue-50 dark:bg-blue-900/30 rounded-full mb-2">
                  <ShieldCheckIcon className="h-5 w-5 text-blue-500" />
                </div>
                <h3 className="text-sm font-semibold text-gray-800 dark:text-white">
                  New Device Detected
                </h3>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                  {deviceOtpMessage || 'A verification code was sent to your email.'}
                </p>
                {deviceVerification.device_brief && (
                  <p className="text-[10px] text-gray-400 mt-1 italic">
                    {deviceVerification.device_brief}
                  </p>
                )}
              </div>
              <form onSubmit={handleDeviceOtpVerify} className="space-y-3">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength="6"
                  placeholder="Enter 6-digit code"
                  value={deviceOtpCode}
                  onChange={(e) => setDeviceOtpCode(e.target.value.replace(/\D/g, ''))}
                  className="w-full text-center text-lg tracking-[0.5em] font-semibold px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  autoFocus
                />
                <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <input
                    type="checkbox"
                    checked={rememberDevice}
                    onChange={(e) => setRememberDevice(e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span>Trust this device for future logins</span>
                </label>
                <button
                  type="submit"
                  disabled={deviceOtpLoading || deviceOtpCode.length !== 6}
                  className="w-full py-2.5 text-xs font-semibold text-white bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg disabled:opacity-50 flex items-center justify-center gap-1.5"
                >
                  {deviceOtpLoading ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <ShieldCheckIcon className="w-3.5 h-3.5" />
                      Verify & Sign In
                    </>
                  )}
                </button>
              </form>
              <div className="flex items-center justify-between text-[11px]">
                <button
                  type="button"
                  onClick={handleDeviceOtpResend}
                  disabled={deviceOtpLoading}
                  className="text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50"
                >
                  Resend code
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDeviceVerification(null);
                    setDeviceOtpCode('');
                    setError('');
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ← Back to login
                </button>
              </div>
            </div>
          ) : google2faPending ? (
            <div className="space-y-3">
              <div className="text-center mb-3">
                <div className="inline-flex items-center justify-center w-11 h-11 bg-teal-50 dark:bg-teal-900/30 rounded-full mb-2">
                  <ShieldCheckIcon className="h-5 w-5 text-teal-500" />
                </div>
                <h3 className="text-sm font-semibold text-gray-800 dark:text-white">
                  Enter 2FA Code
                </h3>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                  Google verified your identity. Enter the 6-digit code from your authenticator app.
                </p>
              </div>

              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => { if (e.key === 'Enter' && twoFactorCode.length === 6) handleGoogleVerify2FA(); }}
                className="w-full px-3 py-2.5 text-center text-xl font-bold tracking-[0.4em] rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-4 focus:ring-teal-500/40 focus:border-teal-500 transition-all duration-150 cursor-text"
                placeholder="000000"
                autoFocus
              />

              <button
                type="button"
                onClick={handleGoogleVerify2FA}
                disabled={twoFactorLoading || twoFactorCode.length !== 6}
                className="w-full py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-sm font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
              >
                {twoFactorLoading ? 'Verifying…' : 'Verify & Continue'}
              </button>

              <button
                type="button"
                onClick={() => {
                  setGoogle2faPending(null);
                  setTwoFactorCode('');
                  setError('');
                  navigate('/portal/dashboard');
                }}
                className="w-full text-[11px] text-gray-500 hover:text-gray-700"
              >
                ← Back to portal
              </button>
            </div>
          ) : (
          <>
          {/* Google Sign-In */}
          {GOOGLE_CLIENT_ID && (
            <div className="space-y-2.5">
              <div className="relative flex items-center my-1">
                <div className="flex-grow border-t border-gray-200"></div>
                <span className="px-2 text-[10px] text-gray-400">or</span>
                <div className="flex-grow border-t border-gray-200"></div>
              </div>
              <button
                type="button"
                onClick={() => handleGoogleSignIn()}
                disabled={googleLoading || loading}
                className="mx-auto flex items-center justify-center gap-2 py-1.5 px-5 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 text-[11px] font-medium text-gray-700 transition-all disabled:opacity-50"
              >
                {googleLoading ? (
                  <svg className="animate-spin h-3.5 w-3.5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="h-4 w-4" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                )}
                <span>{googleLoading ? 'Signing in…' : 'Continue with Google'}</span>
              </button>
              {/* Hidden container for Google Identity Services renderButton */}
              <div ref={googleButtonRef} style={{ position: 'absolute', left: -9999, top: -9999, width: 0, height: 0, overflow: 'hidden' }} />
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">

            <div>
              <label className="block text-[10px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide mb-1">
                {t('login.username')}
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-[1px] focus:ring-teal-400/30 focus:border-teal-400 bg-white dark:bg-gray-700 hover:border-gray-300 shadow-sm"
                placeholder={t('login.username')}
                required
              />
            </div>

            <div>
              <label className="block text-[10px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide mb-1">
                {t('login.password')}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 pr-9 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-[1px] focus:ring-teal-400/30 focus:border-teal-400 bg-white dark:bg-gray-700 hover:border-gray-300 shadow-sm"
                  placeholder={t('login.password')}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-teal-500 transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-4 w-4" />
                  ) : (
                    <EyeIcon className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between pt-0.5">
              <label className="flex items-center cursor-pointer group">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="rounded text-teal-500 border-gray-300 focus:ring-teal-400 w-3.5 h-3.5"
                />
                <span className="ml-1.5 text-[11px] text-gray-600 dark:text-gray-400 group-hover:text-gray-800 transition-colors">{t('login.rememberMe')}</span>
              </label>
              <Link to="/forgot-password" className="text-[11px] text-teal-500 hover:text-teal-600 font-medium transition-colors">
                {t('login.forgotPassword')}
              </Link>
            </div>

            {/* Cloudflare Turnstile CAPTCHA — shown after repeated failures */}
            {captchaRequired && TURNSTILE_SITE_KEY && (
              <div className="flex justify-center py-1.5">
                <div
                  ref={captchaRef}
                  className="cf-turnstile"
                  data-sitekey={TURNSTILE_SITE_KEY}
                  data-theme="light"
                  data-callback={(token) => setCaptchaToken(token)}
                  data-expired-callback={() => setCaptchaToken(null)}
                  data-error-callback={() => setCaptchaToken(null)}
                />
              </div>
            )}
            {captchaRequired && !TURNSTILE_SITE_KEY && (
              <p className="text-[11px] text-amber-600 text-center">
                Additional verification may be required. Please try again.
              </p>
            )}

            <button
              type="submit"
              disabled={loading || (captchaRequired && !captchaToken)}
              className="w-full bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-semibold py-2 rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm mt-1"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('login.signIn')}…
                </span>
              ) : t('login.signIn')}
            </button>
          </form>

          <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 text-center">
            <p className="text-[10px] text-gray-500 dark:text-gray-400 mb-1">Need help?</p>
            <div className="flex items-center justify-center gap-1.5">
              <svg className="h-3.5 w-3.5 text-green-600" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
              </svg>
              <p className="text-[11px] text-gray-700 dark:text-gray-300 font-medium">
                +256-767-641-749
              </p>
            </div>
            <p className="text-[11px] text-gray-600 dark:text-gray-400 mt-1">
              ospitoplus@gmail.com
            </p>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
